import { WeekTimeGrid } from './WeekTimeGrid';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

// UI Components
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { SafeSelect as Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/SafeSelect';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// Icons
import { 
  Calendar,
  Clock,
  User,
  BookOpen,
  MapPin,
  Edit3,
  Trash2,
  Users
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyTimetable = ({ user }) => {
  const [timetables, setTimetables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [selectedTimetable, setSelectedTimetable] = useState(null);
  const [moveForm, setMoveForm] = useState({
    day_of_week: '',
    start_time: '',
    end_time: '',
    room: ''
  });

  // Informations des jours de la semaine
  const daysOfWeek = [
    { value: 0, label: 'Lundi', short: 'LUN' },
    { value: 1, label: 'Mardi', short: 'MAR' },
    { value: 2, label: 'Mercredi', short: 'MER' },
    { value: 3, label: 'Jeudi', short: 'JEU' },
    { value: 4, label: 'Vendredi', short: 'VEN' },
    { value: 5, label: 'Samedi', short: 'SAM' },
    { value: 6, label: 'Dimanche', short: 'DIM' }
  ];

  useEffect(() => {
    fetchMyTimetable();
  }, []);

  const fetchMyTimetable = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/timetables/my-schedule`);
      setTimetables(response.data);
    } catch (error) {
      console.error('Error fetching timetable:', error);
      toast.error('Erreur lors du chargement de l\'emploi du temps');
    } finally {
      setLoading(false);
    }
  };

  const getDayName = (dayNum) => {
    const day = daysOfWeek.find(d => d.value === dayNum);
    return day ? day.label : 'Inconnu';
  };

  const getShortDayName = (dayNum) => {
    const day = daysOfWeek.find(d => d.value === dayNum);
    return day ? day.short : 'INC';
  };

  const formatTime = (time) => {
    return time; // Déjà au format HH:MM
  };

  const canManageTimetables = () => {
    return user.role === 'ADMIN' || user.role === 'PRINCIPAL';
  };

  const handleMoveTimetable = async (e) => {
    e.preventDefault();
    
    if (!selectedTimetable) return;

    try {
      setLoading(true);
      const updateData = {};
      
      if (moveForm.day_of_week) updateData.day_of_week = parseInt(moveForm.day_of_week);
      if (moveForm.start_time) updateData.start_time = moveForm.start_time;
      if (moveForm.end_time) updateData.end_time = moveForm.end_time;
      if (moveForm.room !== selectedTimetable.room) updateData.room = moveForm.room;

      await axios.put(`${API}/timetables/${selectedTimetable.id}/move`, updateData);
      
      toast.success('Créneau déplacé avec succès');
      setMoveDialogOpen(false);
      setSelectedTimetable(null);
      setMoveForm({ day_of_week: '', start_time: '', end_time: '', room: '' });
      await fetchMyTimetable();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur lors du déplacement';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelTimetable = async (timetableId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler ce créneau ?')) {
      return;
    }

    try {
      await axios.delete(`${API}/timetables/${timetableId}/cancel`);
      toast.success('Créneau annulé avec succès');
      await fetchMyTimetable();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur lors de l\'annulation';
      toast.error(errorMsg);
    }
  };

  const openMoveDialog = (timetable) => {
    setSelectedTimetable(timetable);
    setMoveForm({
      day_of_week: timetable.day_of_week.toString(),
      start_time: timetable.start_time,
      end_time: timetable.end_time,
      room: timetable.room || ''
    });
    setMoveDialogOpen(true);
  };

  // Organiser les créneaux par jour
  const timetablesByDay = daysOfWeek.reduce((acc, day) => {
    acc[day.value] = timetables.filter(t => t.day_of_week === day.value)
      .sort((a, b) => a.start_time.localeCompare(b.start_time));
    return acc;
  }, {});

  // Vue grille positionnée à la minute (créneaux de 30 min lisibles)
  const GridView = () => (
    <WeekTimeGrid
      days={daysOfWeek}
      slots={timetables}
      renderSlot={(t, h) => (
        <div className="text-[11px] leading-tight" title={`${t.subject_info?.name || ''} — ${t.class_info?.name || ''} — ${t.start_time}-${t.end_time}`}>
          <div className="font-semibold text-blue-900 truncate">{t.subject_info?.name || 'Matière'}</div>
          <div className="text-gray-600 truncate">{t.start_time} - {t.end_time}</div>
          {user.role === 'TEACHER' && <div className="text-blue-700 truncate">{t.class_info?.name}</div>}
          {user.role !== 'TEACHER' && h > 60 && <div className="text-blue-700 truncate">{t.teacher_info?.first_name} {t.teacher_info?.last_name}</div>}
          {user.role === 'PARENT' && h > 80 && <div className="text-green-700 truncate">{t.student_info?.first_name}</div>}
          {t.room && h > 90 && <div className="text-gray-500 truncate">{/^salle/i.test(t.room) ? t.room : `Salle ${t.room}`}</div>}
          {canManageTimetables() && h > 70 && (
            <div className="flex justify-end space-x-1">
              <Button size="sm" variant="ghost" className="h-5 w-5 p-0" onClick={() => openMoveDialog(t)}><Edit3 className="w-3 h-3" /></Button>
              <Button size="sm" variant="ghost" className="h-5 w-5 p-0 text-red-600" onClick={() => handleCancelTimetable(t.id)}><Trash2 className="w-3 h-3" /></Button>
            </div>
          )}
        </div>
      )}
    />
  );

  // Vue liste
  const ListView = () => (
    <div className="space-y-6">
      {daysOfWeek.map((day) => {
        const dayTimetables = timetablesByDay[day.value] || [];
        
        if (dayTimetables.length === 0) return null;
        
        return (
          <Card key={day.value}>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Calendar className="w-5 h-5 mr-2" />
                {day.label}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {dayTimetables.map((timetable) => (
                  <Card key={timetable.id} className="bg-gray-50">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center">
                            <Clock className="w-4 h-4 mr-2 text-blue-600" />
                            <span className="font-medium">
                              {formatTime(timetable.start_time)} - {formatTime(timetable.end_time)}
                            </span>
                          </div>
                          
                          <div className="flex items-center">
                            <BookOpen className="w-4 h-4 mr-2 text-green-600" />
                            <span className="font-medium">
                              {timetable.subject_info?.name}
                            </span>
                          </div>
                          
                          {user.role !== 'TEACHER' && (
                            <div className="flex items-center">
                              <User className="w-4 h-4 mr-2 text-purple-600" />
                              <span>
                                {timetable.teacher_info?.first_name} {timetable.teacher_info?.last_name}
                              </span>
                            </div>
                          )}
                          
                          {user.role === 'TEACHER' && (
                            <div className="flex items-center">
                              <Users className="w-4 h-4 mr-2 text-orange-600" />
                              <span>
                                {timetable.class_info?.name}
                              </span>
                            </div>
                          )}
                          
                          {user.role === 'PARENT' && (
                            <div className="flex items-center">
                              <User className="w-4 h-4 mr-2 text-green-600" />
                              <span>
                                {timetable.student_info?.first_name} {timetable.student_info?.last_name}
                              </span>
                            </div>
                          )}
                          
                          {timetable.room && (
                            <div className="flex items-center">
                              <MapPin className="w-4 h-4 mr-2 text-red-600" />
                              <span>{timetable.room}</span>
                            </div>
                          )}
                        </div>
                        
                        {canManageTimetables() && (
                          <div className="flex space-x-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openMoveDialog(timetable)}
                            >
                              <Edit3 className="w-4 h-4 mr-2" />
                              Déplacer
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => handleCancelTimetable(timetable.id)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Annuler
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );

  const getPageTitle = () => {
    switch (user.role) {
      case 'TEACHER':
        return 'Mon Emploi du Temps';
      case 'STUDENT':
        return 'Mon Emploi du Temps';
      case 'PARENT':
        return 'Emploi du Temps de mes Enfants';
      case 'ADMIN':
      case 'PRINCIPAL':
        return 'Vue Globale des Emplois du Temps';
      default:
        return 'Emploi du Temps';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-600">Chargement de l'emploi du temps...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{getPageTitle()}</h1>
          <p className="text-gray-600 mt-1">
            {timetables.length} créneau{timetables.length !== 1 ? 's' : ''} programmé{timetables.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Contenu principal */}
      {timetables.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Aucun créneau programmé
            </h3>
            <p className="text-gray-600">
              {user.role === 'TEACHER' && "Aucun cours ne vous est encore assigné."}
              {user.role === 'STUDENT' && "Votre emploi du temps n'est pas encore disponible."}
              {user.role === 'PARENT' && "L'emploi du temps de vos enfants n'est pas encore disponible."}
              {(user.role === 'ADMIN' || user.role === 'PRINCIPAL') && "Aucun emploi du temps configuré."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="grid" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="grid">Vue Grille</TabsTrigger>
            <TabsTrigger value="list">Vue Liste</TabsTrigger>
          </TabsList>
          
          <TabsContent value="grid" className="space-y-4">
            <GridView />
          </TabsContent>
          
          <TabsContent value="list" className="space-y-4">
            <ListView />
          </TabsContent>
        </Tabs>
      )}

      {/* Dialog de déplacement */}
      <Dialog open={moveDialogOpen} onOpenChange={setMoveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Déplacer le Créneau</DialogTitle>
            <DialogDescription>
              Modifiez les détails du créneau pour le déplacer vers un nouveau jour et horaire.
            </DialogDescription>
          </DialogHeader>
          
          {selectedTimetable && (
            <form onSubmit={handleMoveTimetable} className="space-y-4">
              <div className="space-y-2">
                <Label>Matière actuelle</Label>
                <div className="p-3 bg-gray-50 rounded-md">
                  <span className="font-medium">{selectedTimetable.subject_info?.name}</span>
                  {user.role === 'TEACHER' && (
                    <span className="ml-2 text-gray-600">- {selectedTimetable.class_info?.name}</span>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="move_day">Jour de la semaine</Label>
                <Select value={moveForm.day_of_week} onValueChange={(value) => setMoveForm({ ...moveForm, day_of_week: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionnez un jour" />
                  </SelectTrigger>
                  <SelectContent>
                    {daysOfWeek.map((day) => (
                      <SelectItem key={day.value} value={day.value.toString()}>
                        {day.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="move_start_time">Heure de début</Label>
                  <Input
                    id="move_start_time"
                    type="time"
                    value={moveForm.start_time}
                    onChange={(e) => setMoveForm({ ...moveForm, start_time: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="move_end_time">Heure de fin</Label>
                  <Input
                    id="move_end_time"
                    type="time"
                    value={moveForm.end_time}
                    onChange={(e) => setMoveForm({ ...moveForm, end_time: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="move_room">Salle (optionnel)</Label>
                <Input
                  id="move_room"
                  value={moveForm.room}
                  onChange={(e) => setMoveForm({ ...moveForm, room: e.target.value })}
                  placeholder="Ex: Salle A101"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setMoveDialogOpen(false)}
                >
                  Annuler
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Déplacement...' : 'Déplacer le Créneau'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyTimetable;